import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, eur, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Plus, Trash2, Gift, Save } from "lucide-react";

const KIND_LABEL = {
  discount_pct: "Sconto %", free_drink: "Drink omaggio", free_dessert: "Dolce omaggio",
  free_coffee: "Caffè omaggio", free_cocktail: "Cocktail omaggio",
  free_experience: "Esperienza", custom: "Personalizzato",
};

export default function AdminLoyalty() {
  const [conf, setConf] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/loyalty/config");
      setConf(r.data || { enabled: true, earn_cents_per_point: 100, rewards: [] });
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setReward = (i, next) => setConf({ ...conf,
    rewards: conf.rewards.map((r, idx) => idx === i ? next : r) });
  const addReward = () => setConf({ ...conf,
    rewards: [...(conf.rewards || []), { name: "", kind: "custom",
      points_cost: 100, value_cents: null, active: true }] });
  const rmReward = (i) => setConf({ ...conf,
    rewards: conf.rewards.filter((_, idx) => idx !== i) });

  const save = async () => {
    for (const r of conf.rewards || []) {
      if (!r.name.trim()) return toast.error("Ogni premio deve avere un nome");
      if (r.points_cost < 0) return toast.error("Il costo in punti non può essere negativo");
    }
    setSaving(true);
    try {
      await api.patch("/admin/loyalty/config", {
        enabled: conf.enabled,
        earn_cents_per_point: parseInt(conf.earn_cents_per_point) || 100,
        rewards: (conf.rewards || []).map((r) => ({
          ...r, points_cost: parseInt(r.points_cost) || 0,
          value_cents: r.value_cents === "" || r.value_cents === null
                       ? null : parseInt(r.value_cents),
        })),
      });
      toast.success("Configurazione loyalty salvata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!conf) return <div className="p-6 text-ink-500">Caricamento…</div>;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="loyalty-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/admin/customers" className="btn-outline text-xs py-1 px-3" data-testid="loyalty-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Clienti
        </Link>
        <div>
          <h1 className="font-serif text-3xl flex items-center gap-2">
            <Sparkles className="w-6 h-6" /> Loyalty
          </h1>
          <p className="text-xs text-ink-500">
            Sistema punti + premi. I punti sono calcolati automaticamente sul totale speso di ogni cliente.
          </p>
        </div>
      </div>

      <section className="rounded-2xl border border-sand-300 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label>Programma loyalty attivo</Label>
            <div className="text-xs text-ink-500">
              Se disattivi, i punti restano ma non vengono più mostrati al cliente.
            </div>
          </div>
          <Switch checked={!!conf.enabled}
                  onCheckedChange={(v) => setConf({ ...conf, enabled: v })}
                  data-testid="loyalty-enabled" />
        </div>
        <div>
          <Label>1 punto ogni…</Label>
          <div className="flex items-center gap-2">
            <Input type="number" min="10" step="10" className="max-w-[140px]"
                   value={((conf.earn_cents_per_point || 100) / 100).toFixed(2)}
                   onChange={(e) => setConf({ ...conf,
                     earn_cents_per_point: Math.round(parseFloat(e.target.value || 0) * 100) })}
                   data-testid="loyalty-earn-rate" />
            <span className="text-sm text-ink-500">€ spesi</span>
          </div>
          <div className="text-xs text-ink-500 mt-1">
            Attuale: 1 punto ogni {eur(conf.earn_cents_per_point)}.
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-sand-300 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold flex items-center gap-1"><Gift className="w-4 h-4" /> Premi ({(conf.rewards || []).length})</h2>
          <Button size="sm" variant="outline" onClick={addReward} data-testid="loyalty-add-reward">
            <Plus className="w-3 h-3 mr-1" /> Premio
          </Button>
        </div>
        {(!conf.rewards || conf.rewards.length === 0) ? (
          <div className="text-xs text-ink-500 italic p-3 rounded-xl border border-dashed border-sand-300 text-center"
               data-testid="loyalty-rewards-empty">
            Nessun premio ancora. Aggiungine uno per iniziare a "regalare".
          </div>
        ) : (
          <div className="space-y-2">
            {conf.rewards.map((r, i) => (
              <div key={i} className="rounded-xl border border-sand-300 p-3 grid grid-cols-12 gap-2 items-end"
                   data-testid={`loyalty-reward-${i}`}>
                <div className="col-span-4">
                  <Label className="text-xs">Nome</Label>
                  <Input value={r.name} onChange={(e) => setReward(i, { ...r, name: e.target.value })}
                         data-testid={`loyalty-reward-name-${i}`} />
                </div>
                <div className="col-span-3">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={r.kind} onValueChange={(v) => setReward(i, { ...r, kind: v })}>
                    <SelectTrigger data-testid={`loyalty-reward-kind-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(KIND_LABEL).map(([k, l]) => (
                        <SelectItem key={k} value={k}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Costo punti</Label>
                  <Input type="number" min="0" value={r.points_cost}
                         onChange={(e) => setReward(i, { ...r, points_cost: parseInt(e.target.value) || 0 })}
                         data-testid={`loyalty-reward-cost-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Valore (€, opz.)</Label>
                  <Input type="number" step="0.10"
                         value={r.value_cents !== null && r.value_cents !== undefined
                                ? (r.value_cents / 100).toFixed(2) : ""}
                         onChange={(e) => setReward(i, { ...r,
                           value_cents: e.target.value === "" ? null
                                        : Math.round(parseFloat(e.target.value) * 100) })}
                         data-testid={`loyalty-reward-value-${i}`} />
                </div>
                <div className="col-span-1 flex flex-col items-center gap-1">
                  <Switch checked={r.active !== false}
                          onCheckedChange={(v) => setReward(i, { ...r, active: v })}
                          data-testid={`loyalty-reward-active-${i}`} />
                  <button type="button" onClick={() => rmReward(i)}
                          className="text-red-700 p-1 hover:bg-red-50 rounded"
                          data-testid={`loyalty-reward-del-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="loyalty-save">
          <Save className="w-4 h-4 mr-1" />
          {saving ? "Salvo…" : "Salva configurazione"}
        </Button>
      </div>
    </div>
  );
}
